/**
 * Last Cycle Count Indicator Component
 * 
 * Displays when an item was last cycle counted in the current location.
 * Shows relative time (e.g., "3 days ago") with full date tooltip.
 * 
 * Note: Backend uses /api/audits routes (alias approach - DB tables remain unchanged)
 */
import React from 'react';
import { Clock, ClipboardCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatAuditTimestamp, getTenantTimezone } from '../../lib/timezone';

export function LastCycleCountIndicator({ lastAuditedAt, className = '', badge = false }) {
  if (!lastAuditedAt) {
    if (badge) {
      return (
        <span className={`text-[11px] font-medium text-muted-foreground bg-muted px-1.5 py-px rounded-full inline-flex items-center gap-1 leading-tight ${className}`} data-testid="cycle-count-badge">
          <ClipboardCheck className="w-2.5 h-2.5" />
          Never counted
        </span>
      );
    }
    return (
      <span className={`text-xs text-muted-foreground flex items-center gap-1 ${className}`}>
        <ClipboardCheck className="w-3 h-3" />
        Never counted
      </span>
    );
  }

  // Calculate relative time
  const getRelativeTime = () => {
    const timeZone = getTenantTimezone();
    const auditDate = new Date(new Date(lastAuditedAt).toLocaleString('en-US', { timeZone }));
    const now = new Date(new Date().toLocaleString('en-US', { timeZone }));
    const diffMs = now - auditDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Get color based on recency
  const getColorClass = () => {
    const auditDate = new Date(lastAuditedAt);
    const now = new Date();
    const diffDays = Math.floor((now - auditDate) / (1000 * 60 * 60 * 24));

    if (diffDays <= 7) return 'text-green-600';  // Recent - good
    if (diffDays <= 30) return 'text-yellow-600'; // Getting stale
    return 'text-red-600'; // Overdue
  };

  if (badge) {
    const colorMap = {
      'text-green-600': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'text-yellow-600': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'text-red-600': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    const badgeColor = colorMap[getColorClass()] || 'bg-muted text-muted-foreground';
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`text-[11px] font-medium px-1.5 py-px rounded-full inline-flex items-center gap-1 cursor-help leading-tight ${badgeColor} ${className}`} data-testid="cycle-count-badge">
              <Clock className="w-3 h-3" />
              Counted {getRelativeTime()}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Last cycle count: {formatAuditTimestamp(lastAuditedAt)}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`text-xs flex items-center gap-1 cursor-help ${getColorClass()} ${className}`}>
            <Clock className="w-3 h-3" />
            Counted {getRelativeTime()}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Last cycle count: {formatAuditTimestamp(lastAuditedAt)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default LastCycleCountIndicator;
